import type { PracticeDifficulty } from "./practiceQuestionTemplates";
import { PRACTICE_QUESTION_TEMPLATES } from "./practiceQuestionTemplates";
import { PRACTICE_GENERATOR_CATALOG } from "./practiceGeneratorCatalog";
import {
  emptySkillRecord,
  type PracticeMasteryState,
  type PracticeSkillRecord,
} from "./practiceMastery";

/** Misses or successes in a row before difficulty changes. */
export const PRACTICE_ADAPTIVE_STREAK = 3;

const DIFFICULTY_ORDER: PracticeDifficulty[] = ["intro", "core", "challenge"];

/**
 * One easier skill. After three failures at intro, practice moves to that
 * skill's intro (or its easiest exercise when intro is not generated yet).
 */
export const PRACTICE_SKILL_PREREQUISITE: Readonly<Record<string, string>> = {
  "arithmetic.add-within-5": "arithmetic.count-on",
  "arithmetic.add-within-10": "arithmetic.add-within-5",
  "arithmetic.add-two-digit-no-carry": "arithmetic.add-within-10",
  "arithmetic.add-carry": "arithmetic.add-two-digit-no-carry",
  "arithmetic.add-carry-hundreds": "arithmetic.add-carry",
  "arithmetic.sub-within-10": "arithmetic.add-within-10",
  "arithmetic.sub-two-digit-no-borrow": "arithmetic.sub-within-10",
  "arithmetic.sub-borrow": "arithmetic.sub-two-digit-no-borrow",
  "arithmetic.sub-borrow-hundreds": "arithmetic.sub-borrow",
  "arithmetic.place-hundreds": "arithmetic.place-tens",
  "arithmetic.multiply-easy": "arithmetic.add-within-10",
  "arithmetic.multiply-core": "arithmetic.multiply-easy",
  "arithmetic.multiply-hard": "arithmetic.multiply-core",
  "arithmetic.divide-exact": "arithmetic.multiply-easy",
  "arithmetic.divide-exact-core": "arithmetic.divide-exact",
  "arithmetic.divide-remainder": "arithmetic.divide-exact-core",
  "arithmetic.compare-fractions-same-num": "arithmetic.compare-fractions-same-den",
  "arithmetic.add-fractions-same-den": "arithmetic.compare-fractions-same-den",
  "arithmetic.add-fractions-related": "arithmetic.add-fractions-same-den",
  "arithmetic.equivalent-missing": "arithmetic.equivalent-fractions",
  "arithmetic.decimals-add": "arithmetic.decimals-compare",
  "arithmetic.percent-simple-core": "arithmetic.percent-simple",
  "arithmetic.percent-increase": "arithmetic.percent-of",
  "algebra.system": "algebra.linear-one-step",
  "algebra.linear-inequality": "algebra.linear-one-step",
  "algebra.slope": "algebra.linear-one-step",
  "algebra.factor-quadratic": "algebra.expand-binomial",
  "algebra.vertex-form": "algebra.factor-quadratic",
  "algebra.exponent-power": "algebra.exponent-product",
  "algebra.exponent-quotient": "algebra.exponent-product",
  "algebra.scientific-notation": "algebra.exponent-product",
  "algebra.zero-negative-exponent": "algebra.exponent-product",
  "algebra.logarithm": "algebra.zero-negative-exponent",
  "algebra.function-value": "algebra.linear-one-step",
  "algebra.evaluate-linear": "algebra.linear-one-step",
  "algebra.absolute-value": "algebra.linear-one-step",
  "algebra.slope-intercept-line": "algebra.slope",
  "algebra.solve-quadratic": "algebra.factor-quadratic",
  "algebra.quadratic-formula": "algebra.solve-quadratic",
  "algebra.rational-simplify": "algebra.factor-quadratic",
  "algebra.rational-exponent": "algebra.exponent-power",
  "algebra.exponential-value": "algebra.exponent-power",
  "sequences.geometric-term": "sequences.arithmetic-term",
  "trig.special-right": "trig.exact-30-60",
  "complex.multiply": "complex.add",
  "geometry.circle-area": "geometry.circle-circumference",
  "geometry.pythagorean": "geometry.triangle-area",
  "trig.right-ratio": "geometry.pythagorean",
  "trig.inverse-special": "trig.exact-30-60",
  "calc.product-rule": "calc.power-rule",
  "calc.chain-rule": "calc.power-rule",
  "calc.definite-integral": "calc.indefinite-integral",
  "vectors.dot": "vectors.add",
  "vectors.magnitude": "vectors.add",
  "complex.modulus": "complex.add",
};

export type AdaptiveStepKind = "down" | "up" | "prereq" | "same";

export type AdaptiveSkillOffer = {
  skillId: string;
  difficulty: PracticeDifficulty;
};

export type AdaptiveDecision = {
  skillId: string;
  difficulty: PracticeDifficulty;
  stepped: AdaptiveStepKind;
  showWorkedExample: boolean;
};

export type AdaptiveWorkedExample = {
  prompt: string;
  expectedAnswer: string;
  explanation: string;
};

export function prerequisiteSkill(skillId: string): string | undefined {
  const next = PRACTICE_SKILL_PREREQUISITE[skillId];
  if (!next || next === skillId) return undefined;
  return next;
}

export function adaptiveOffers(): AdaptiveSkillOffer[] {
  const seen = new Set<string>();
  const offers: AdaptiveSkillOffer[] = [];
  const add = (skillId: string, difficulty: PracticeDifficulty) => {
    const key = `${skillId}:${difficulty}`;
    if (seen.has(key)) return;
    seen.add(key);
    offers.push({ skillId, difficulty });
  };
  for (const row of PRACTICE_GENERATOR_CATALOG) add(row.skillId, row.difficulty);
  for (const row of PRACTICE_QUESTION_TEMPLATES) {
    add(row.skillId, row.difficulty ?? "core");
  }
  return offers;
}

function difficultyRank(difficulty: PracticeDifficulty): number {
  const idx = DIFFICULTY_ORDER.indexOf(difficulty);
  return idx < 0 ? 99 : idx;
}

function difficultiesFor(
  skillId: string,
  offers: readonly AdaptiveSkillOffer[],
): PracticeDifficulty[] {
  return DIFFICULTY_ORDER.filter((difficulty) =>
    offers.some((offer) => offer.skillId === skillId && offer.difficulty === difficulty),
  );
}

function lowerDifficulty(
  current: PracticeDifficulty,
  available: readonly PracticeDifficulty[],
): PracticeDifficulty | null {
  const idx = available.indexOf(current);
  if (idx <= 0) return null;
  return available[idx - 1] ?? null;
}

function higherDifficulty(
  current: PracticeDifficulty,
  available: readonly PracticeDifficulty[],
): PracticeDifficulty | null {
  const idx = available.indexOf(current);
  if (idx < 0 || idx >= available.length - 1) return null;
  return available[idx + 1] ?? null;
}

function easiestDifficulty(
  available: readonly PracticeDifficulty[],
): PracticeDifficulty {
  return available[0] ?? "intro";
}

export function decideAdaptiveStep(
  record: PracticeSkillRecord,
  skillId: string,
  offers: readonly AdaptiveSkillOffer[] = adaptiveOffers(),
): AdaptiveDecision {
  const available = difficultiesFor(skillId, offers);
  const current = available.includes(record.difficulty)
    ? record.difficulty
    : easiestDifficulty(available);

  if (record.failStreak >= PRACTICE_ADAPTIVE_STREAK) {
    const lower = lowerDifficulty(current, available);
    if (lower) {
      return {
        skillId,
        difficulty: lower,
        stepped: "down",
        showWorkedExample: true,
      };
    }
    const prereq = prerequisiteSkill(skillId);
    if (prereq) {
      const prereqLevels = difficultiesFor(prereq, offers);
      return {
        skillId: prereq,
        difficulty: prereqLevels.includes("intro")
          ? "intro"
          : easiestDifficulty(prereqLevels),
        stepped: "prereq",
        showWorkedExample: true,
      };
    }
    return {
      skillId,
      difficulty: available.includes("intro") ? "intro" : current,
      stepped: "same",
      showWorkedExample: true,
    };
  }

  if (record.successStreak >= PRACTICE_ADAPTIVE_STREAK) {
    const higher = higherDifficulty(current, available);
    if (higher) {
      return {
        skillId,
        difficulty: higher,
        stepped: "up",
        showWorkedExample: false,
      };
    }
    const back = record.resumeSkillId?.trim();
    if (back && back !== skillId) {
      const backLevels = difficultiesFor(back, offers);
      if (backLevels.length) {
        return {
          skillId: back,
          difficulty: backLevels.includes("intro")
            ? "intro"
            : easiestDifficulty(backLevels),
          stepped: "up",
          showWorkedExample: false,
        };
      }
    }
  }

  return {
    skillId,
    difficulty: current,
    stepped: "same",
    showWorkedExample: false,
  };
}

export function applyAdaptiveStep(
  state: PracticeMasteryState,
  skillId: string,
  offers?: readonly AdaptiveSkillOffer[],
): { state: PracticeMasteryState; decision: AdaptiveDecision } {
  const record = state.skills[skillId] ?? emptySkillRecord();
  const decision = decideAdaptiveStep(record, skillId, offers);
  const changed = decision.showWorkedExample || decision.stepped !== "same";
  if (!changed) return { state, decision };

  const skills = { ...state.skills };
  const dest = skills[decision.skillId] ?? emptySkillRecord();
  const steppedAway = decision.skillId !== skillId && decision.stepped !== "up";
  const resumeSkillId = steppedAway
    ? skillId
    : decision.skillId === skillId
      ? record.resumeSkillId
      : undefined;
  skills[decision.skillId] = {
    ...dest,
    difficulty: decision.difficulty,
    failStreak: 0,
    successStreak: 0,
    ...(resumeSkillId ? { resumeSkillId } : {}),
  };
  if (decision.skillId !== skillId && skills[skillId]) {
    skills[skillId] = {
      ...skills[skillId],
      failStreak: 0,
      successStreak: 0,
    };
  }
  return { state: { ...state, skills }, decision };
}

/** A generator when one exists, otherwise the static catalog exercise. */
export function resolveAdaptiveExercise(
  skillId: string,
  difficulty: PracticeDifficulty,
): { generatorId: string; grade: number } | { templateUuid: string } | null {
  const generators = PRACTICE_GENERATOR_CATALOG.filter(
    (row) => row.skillId === skillId,
  ).sort((a, b) => difficultyRank(a.difficulty) - difficultyRank(b.difficulty));
  const generator =
    generators.find((row) => row.difficulty === difficulty) ??
    generators.find((row) => row.difficulty === "intro") ??
    generators[0];
  if (generator) {
    return { generatorId: generator.id, grade: generator.gradeMin };
  }

  const templates = PRACTICE_QUESTION_TEMPLATES.filter(
    (row) => row.skillId === skillId,
  ).sort(
    (a, b) =>
      difficultyRank(a.difficulty ?? "core") - difficultyRank(b.difficulty ?? "core"),
  );
  const template =
    templates.find((row) => (row.difficulty ?? "core") === difficulty) ??
    templates[0];
  if (template) return { templateUuid: template.uuid };
  return null;
}

export function describeWorkedExample(
  locale: "en" | "he",
  prompt: string,
  answer: string,
): string {
  if (locale === "he") {
    return `דוגמה פתורה. ${prompt} התשובה היא ${answer}.`;
  }
  return `Worked example. ${prompt} The answer is ${answer}.`;
}
